import {inject, injectable} from 'inversify'
import {ApiError, ApiRequestOptions, IApiResponseHandler} from '@/src/ports/services/api'
import {DI} from '@/src/config/di/types'
import truncate from 'lodash/truncate'
import type {StorageAdapter} from '@/src/core/domain/data/storage'
import type {IInstrumentationService} from '@/src/ports/services/monitoring/instrumentation'
import type {IErrorReporter} from '@/src/ports/services/monitoring'
import type {IEventBus} from '@/src/core/domain/events'
import type {QueryCacheInterface} from '@/src/ports/services/query-cache'
import {PageEvents} from '@/src/core/domain/events/page.events'
import {isMobile} from '@/src/shared/utils/functions'
import * as Sentry from '@sentry/nextjs'
import {redirect} from 'next/navigation'
import {toClientSession} from '@/src/core/domain/session/client-session'
import {getClientSessionLifecycle, pendingSessionRequest} from '@/src/infrastructure/session/client-session-lifecycle'

@injectable()
export class ApiResponseHandler implements IApiResponseHandler {
  @inject(DI.IErrorReporter)
  errorReporter!: IErrorReporter
  @inject(DI.IInstrumentationService)
  instrumentation!: IInstrumentationService
  @inject(DI.IEventBus)
  eventBus!: IEventBus
  @inject(DI.IQueryCacheService)
  queryCache!: QueryCacheInterface

  async redirect(path: string): Promise<never> {
    if (typeof window === 'undefined') {
      return redirect(path)
    }

    getClientSessionLifecycle()?.terminate()
    await this.eventBus.publish(PageEvents.redirect({url: path}))
    // Navigation replaces this page; callers must not continue or run mutation callbacks.
    return pendingSessionRequest()
  }

  protected async processSessionData(data: ApiResponse, _generation: number | undefined) {
    this.syncSessionCache(data)
  }

  protected syncSessionCache(data: ApiResponse) {
    if (typeof window === 'undefined') {
      return
    }

    const service = data.service
    if (!service?.deleteSession && service?.session === undefined) {
      return
    }

    const session = service.deleteSession ? null : service.session
    // Keep private JWT claims and the raw PHP session ID out of the client cache.
    const nextSession = toClientSession(session)
    this.queryCache.setQueryData(['session'], nextSession)

    const layout = this.queryCache.getQueryData<LayoutData>(['layout'])
    if (layout) {
      const header = {...layout.header}
      if (nextSession === null) {
        delete header.cart
      }
      this.queryCache.setQueryData(['layout'], {...layout, header, session: nextSession})
    }
  }

  async handle<ResponseType>(
    response: Response,
    options: ApiRequestOptions,
  ): Promise<ResponseType & Partial<ApiResponse>> {
    const origin = typeof window !== 'undefined' && window.location.origin || ''
    const lifecycle = getClientSessionLifecycle()
    const generation = options.sessionGeneration ?? lifecycle?.capture()
    const stale = () => lifecycle && !lifecycle.isCurrent(generation!)
    if (stale()) return pendingSessionRequest()

    if (options.onResponse) {
      await options.onResponse(response)
    }

    if (stale()) return pendingSessionRequest()
    if (isMobile() && response.status === 401) {
      // HTTP status is authoritative, even for a success envelope or invalid JSON.
      lifecycle?.terminate()
      await this.processSessionData({success: true, data: null, service: {deleteSession: true}}, generation)
      return this.redirect(`${origin}/login/`)
    }

    let data: ApiResponse<ResponseType> | null = null

    try {
      data = (await response.clone().json()) as ApiResponse<ResponseType>
    } catch (e: unknown) {
      this.errorReporter.capture(e)
    }

    if (stale()) return pendingSessionRequest()
    const terminalReset = !options.withoutSession && data?.service?.deleteSession
    if (terminalReset) {
      // Revoke every other request synchronously, before cache/storage cleanup awaits.
      lifecycle?.terminate()
      if (data) data.service = {...data.service, deleteSession: true}
    }
    if (data) {
      if (!options.withoutSession) await this.processSessionData(data, generation)
      if (!terminalReset && stale()) return pendingSessionRequest()
      await options.onResponseData?.(data)
    }
    if (!terminalReset && stale()) return pendingSessionRequest()

    if (response.ok && data?.success) {
      if (data?.redirect) {
        return await this.instrumentation.startSpanAsync(
          {
            name: `Редирект: ${data?.redirect}`,
            attributes: {'redirect.url': data?.redirect},
          },
          async () => {
            Sentry.logger.info(`Редирект: ${origin}${data?.redirect}`)

            return await this.redirect(`${origin}${data?.redirect}`)
          },
        )
      }

      return data.data as ResponseType & Partial<ApiResponse>
    } else if (data && !data.success) {
      let message = data.error.message.includes('API:')
        ? data.error.message
        : `API:[${response.status}]:${data.error.message}`

      if (typeof window !== 'undefined' && message.includes('API:')) {
        message = message.replace(/API:\[\d+]:/, '')
      }

      throw new ApiError(message, response.status)
    } else {
      let text = truncate(await response.clone().text(), {length: 1000})

      if (!text) {
        text = `API:[${response.status}]:Что-то пошло не так, попробуйте позднее`
      }

      throw new ApiError(text, response.status)
    }
  }
}

@injectable()
export class MobileApiResponseHandler extends ApiResponseHandler {
  @inject(DI.IErrorReporter)
  declare errorReporter: IErrorReporter

  @inject(DI.IInstrumentationService)
  declare instrumentation: IInstrumentationService

  @inject(DI.StorageAdapter)
  declare storage: StorageAdapter

  @inject(DI.IEventBus)
  declare eventBus: IEventBus

  @inject(DI.IQueryCacheService)
  declare queryCache: QueryCacheInterface

  protected async processSessionData(data: ApiResponse, generation: number | undefined) {
    this.syncSessionCache(data)
    const lifecycle = getClientSessionLifecycle()
    const mutation = async () => {
      if (data.service?.deleteSession) {
        await this.storage.removeItem('session')
        await this.storage.removeItem('mobileCsrf')
        return
      }
      if (lifecycle && !lifecycle.isCurrent(generation!)) return
      if (data.service?.sessionString) await this.storage.setItem('session', data.service.sessionString)
      if (lifecycle && !lifecycle.isCurrent(generation!)) return
      if (data.service?.mobileCsrf) await this.storage.setItem('mobileCsrf', data.service.mobileCsrf)
    }
    if (lifecycle) await lifecycle.mutateStorage(mutation)
    else await mutation()
  }
}
